// Dependencies
import { Component, ReactElement } from 'react';
import { connect } from 'react-redux';

import LoaderSpinner from 'components/atoms/LoaderSpinner';
import Message from 'components/atoms/Message';
import { PortType, RootState } from 'types';
import { MainControlsInterface, SearchControlsInterface } from 'types/functions';
import { FilterObjectType } from 'types/search';
import { SummaryData } from 'types/system';
import { checkDifferent, clone } from 'utils/formHandlers';
import { translate } from 'utils/translate';

interface ResultMessageComponentProps {
    language: string;
    port: PortType;
    display: string;
    isLoading?: boolean;
    mainControls: MainControlsInterface;
    ishighlightResults?: boolean;
    searchObject: FilterObjectType;
    highlightNumbers?: SummaryData | null;
    initialumbers?: SummaryData;
    limit: number;
    displayId: string;
    searchControls: SearchControlsInterface;
    children?: ReactElement;
}

interface ResultMessageComponentState {
    language: string;
    isLoading?: boolean;
    isSearchResults?: boolean;
    isFoundResults?: boolean;
    searchObject: FilterObjectType;
    ishighlightResults?: boolean;
    foundNumbers: SummaryData | null;
    searchNumbers: SummaryData | null;
    highlightNumbers: SummaryData | null;
    displayId: string;
    display: number;
}

class ResultMessage extends Component<ResultMessageComponentProps, ResultMessageComponentState> {
    constructor(props: ResultMessageComponentProps) {
        super(props);
        this.state = {
            displayId: props.displayId,
            searchObject: clone(props.searchObject),
            isLoading: false,
            foundNumbers: null,
            searchNumbers: null,
            highlightNumbers: null,
            language: 'en',
            display: 0,
        };
    }

    static getDerivedStateFromProps(props: ResultMessageComponentProps, state: ResultMessageComponentState) {
        const foundNumbers = props?.searchControls?.getFoundObjectNumbers
            ? props?.searchControls?.getFoundObjectNumbers()
            : null;
        const searchNumbers = props?.searchControls?.getSearchObjectNumbers
            ? props?.searchControls?.getSearchObjectNumbers()
            : null;

        if (props.displayId !== state.displayId) {
            return {
                displayId: props.displayId,
                foundNumbers,
                searchNumbers,
                isSearchResults: searchNumbers !== null,
                isFoundResults: foundNumbers !== null,
            };
        }
        if (
            props.ishighlightResults !== state.ishighlightResults ||
            props.isLoading !== state.isLoading ||
            checkDifferent(props.highlightNumbers, state.highlightNumbers)
        ) {
            return {
                isLoading: props.isLoading,
                highlightNumbers: props.highlightNumbers,
                foundNumbers,
                searchNumbers,
                isSearchResults: searchNumbers !== null,
                isFoundResults: foundNumbers !== null,
                ishighlightResults: props.ishighlightResults,
                searchObject: clone(props.searchObject),
            };
        }
        return null;
    }

    componentDidMount(): void {
        const { transits } = this.props.mainControls.getTransit(0);
        this.setState({
            display: transits.filter((x) => {
                return x.options.display;
            }).length,
        });
    }

    componentDidUpdate(prevProps: ResultMessageComponentProps, prevState: ResultMessageComponentState) {
        if (prevState.displayId !== this.state.displayId) {
            const { transits } = this.props.mainControls.getTransit(0);
            this.setState({
                display: transits.filter((x) => {
                    return x.options.display;
                }).length,
            });
        }
    }

    formatHighlightMessage = () => {
        if (!this.props.ishighlightResults) {
            return '';
        }

        const { highlightNumbers } = this.state;
        const { port } = this.props;

        if (!highlightNumbers) {
            return '';
        }

        const {
            total = 0,
            forReview = 0,
            simulated = 0,
            ppu = 0,
            inDateRange = 0,
            inDateRangeForReview = 0,
            startDate,
            endDate,
        } = highlightNumbers;

        const foundTransits = total - forReview;
        const xAIS = foundTransits - simulated - ppu;
        const outOfDateRange = inDateRange - inDateRangeForReview;

        const formattedStartDate = startDate ? startDate.formatDateTimeToString('dd LLL yyyy') : null;
        const formattedEndDate = endDate ? endDate.formatDateTimeToString('dd LLL yyyy') : null;

        let message = `${translate('Highlighting')} ${foundTransits}`;

        // if (simulated || ppu) {
        //     message += ` (${xAIS > 0 ? `${xAIS}xAIS, ` : ''}${simulated > 0 ? `${simulated}xSIM, ` : ''}${
        //         ppu > 0 ? `${ppu}xPPU` : ''
        //     })`;
        // }

        if (total !== inDateRange) {
            if (!forReview) {
                message += ` ${translate('out of')} ${outOfDateRange}`;
            } else {
                message += ` ${translate('out of')} ${outOfDateRange} of ${forReview + outOfDateRange}`;
            }
        }

        message += ` ${translate('transits')}`;

        if (total >= 1) {
            message += ` ${translate('between')} ${formattedStartDate || ''} ${translate('and')} ${
                formattedEndDate || ''
            }`;
        }

        if (forReview > 0) {
            message += ` inc ${forReview} ${translate('for review')}`;
        }
        return message.trim();
    };

    formatSearchdMessage = () => {
        if (!this.state.isSearchResults) {
            return '';
        }

        const { searchNumbers, display } = this.state;
        const { port } = this.props;

        if (!searchNumbers) {
            return '';
        }

        const {
            total = 0,
            forReview = 0,
            simulated = 0,
            ppu = 0,
            inDateRange = 0,
            inDateRangeForReview = 0,
            startDate,
            endDate,
        } = searchNumbers;

        const foundTransits = total - forReview;
        const xAIS = foundTransits - simulated - ppu;
        const outOfDateRange = inDateRange - inDateRangeForReview;

        const formattedStartDate = startDate ? startDate.formatDateTimeToString('dd LLL yyyy') : null;
        const formattedEndDate = endDate ? endDate.formatDateTimeToString('dd LLL yyyy') : null;

        let message =
            this.state.searchObject.limit < foundTransits && this.state.searchObject.limit
                ? `${translate('Displaying')} ${this.state.searchObject.limit}/${
                      foundTransits === display ? foundTransits : `${display} ${translate('of')} ${foundTransits}`
                  }`
                : `${translate('Displaying')} ${
                      foundTransits === display ? foundTransits : `${display} ${translate('of')} ${foundTransits}`
                  }`;

        // if (simulated || ppu) {
        //     message += ` (${xAIS > 0 ? `${xAIS}xAIS, ` : ''}${simulated > 0 ? `${simulated}xSIM, ` : ''}${
        //         ppu > 0 ? `${ppu}xPPU` : ''
        //     })`;
        // }

        if (total !== inDateRange) {
            message += ` ${translate('out of')} ${outOfDateRange}`;
        }

        message += ` ${translate('transits')}`;

        if (total >= 1) {
            message += ` ${translate('between')} ${formattedStartDate || ''} ${translate('and')} ${
                formattedEndDate || ''
            }`;
        }

        if (forReview > 0) {
            message += ` ${forReview} ${translate('for review')}`;
        }

        return message.trim();
    };

    formatFoundMessage = () => {
        if (!this.state.isFoundResults) {
            return '';
        }

        const { foundNumbers } = this.state;
        const { port } = this.props;

        if (!foundNumbers) {
            return '';
        }

        const {
            total = 0,
            forReview = 0,
            simulated = 0,
            ppu = 0,
            inDateRange = 0,
            inDateRangeForReview = 0,
            startDate,
            endDate,
        } = foundNumbers;

        const foundTransits = total - forReview;
        const xAIS = foundTransits - simulated - ppu;
        const outOfDateRange = inDateRange - inDateRangeForReview;

        const formattedStartDate = startDate ? startDate.formatDateTimeToString('dd LLL yyyy') : null;
        const formattedEndDate = endDate ? endDate.formatDateTimeToString('dd LLL yyyy') : null;

        let message = `${translate('Found')} ${foundTransits}`;

        // if (simulated || ppu) {
        //     message += ` (${xAIS > 0 ? `${xAIS}xAIS, ` : ''}${simulated > 0 ? `${simulated}xSIM, ` : ''}${
        //         ppu > 0 ? `${ppu}xPPU` : ''
        //     })`;
        // }

        if (total !== inDateRange) {
            message += ` ${translate('out of')} ${outOfDateRange}`;
        }

        message += ` ${translate('transits')}`;

        if (total >= 1) {
            message += ` ${translate('between')} ${formattedStartDate || ''} ${translate('and')} ${
                formattedEndDate || ''
            }`;
        }

        if (forReview > 0) {
            message += ` +${forReview} ${translate('for review')}`;
        }
        if (foundTransits > this.props.limit && this.props.limit !== 0) {
            message += ` ${translate('limit by', { limit: this.props.limit })}`;
        }

        return message.trim();
    };

    formatIitialMessage = (): string => {
        const { initialumbers, port, display } = this.props;

        if (display === 'highlight') {
            return this.formatSearchdMessage();
        }
        // Extract dates
        const startDate = initialumbers?.startDate;
        const endDate = initialumbers?.endDate;

        // Format dates to local time
        const formattedStartDate = startDate
            ? startDate.formatDateTimeToString('dd LLL yyyy')
            : translate('Unknown Start Date');
        const formattedEndDate = endDate
            ? endDate.formatDateTimeToString('dd LLL yyyy')
            : translate('Unknown End Date');

        //@ts-ignore
        const { total = 0, forReview = 0 } = initialumbers;

        const transits = total - forReview;
        const reviewMessage = forReview > 0 ? ` +${forReview} ${translate('for review')}` : '';
        const limitMessage =
            transits > this.props.limit && this.props.limit != 0 ? `${translate('limit by')} ${this.props.limit}` : '';
        return `
            ${transits} ${translate('transits available between')}
            ${formattedStartDate} ${translate('and')} ${formattedEndDate}
            ${reviewMessage} 
            ${limitMessage}
        `;
    };

    renderMessage = (): { type: string; message: ReactElement } => {
        const { foundNumbers, searchNumbers, isFoundResults, ishighlightResults, isSearchResults } = this.state;

        const getLatestMessage = () => {
            if (!isFoundResults && !isSearchResults) {
                return 'initial';
            }

            if (ishighlightResults) {
                return 'highlight';
            }

            if (isFoundResults && isSearchResults) {
                const foundTime = foundNumbers?.time;
                const searchTime = searchNumbers?.time;

                if (foundTime && searchTime) {
                    return foundTime.toTimestamp() > searchTime.toTimestamp() ? 'found' : 'search';
                }
            }

            return isFoundResults ? 'found' : 'search';
        };

        const latestMessageType = getLatestMessage();

        if (latestMessageType === 'found') {
            return {
                type: 'info',
                message: (
                    <div className="text-sm h-10 first-letter-uppercase">
                        <div
                            dangerouslySetInnerHTML={{
                                __html: this.formatFoundMessage(),
                            }}
                        ></div>
                    </div>
                ),
            };
        }

        if (latestMessageType === 'search') {
            return {
                type: 'success',
                message: (
                    <div className="text-sm h-10 first-letter-uppercase">
                        <div
                            dangerouslySetInnerHTML={{
                                __html: this.formatSearchdMessage(),
                            }}
                        ></div>
                    </div>
                ),
            };
        }
        if (latestMessageType === 'highlight') {
            return {
                type: 'warning',
                message: (
                    <div className="text-sm h-10 first-letter-uppercase">
                        <div
                            dangerouslySetInnerHTML={{
                                __html: this.formatHighlightMessage(),
                            }}
                        ></div>
                    </div>
                ),
            };
        }

        return {
            type: 'info',
            message: (
                <div className="text-sm h-10 first-letter-uppercase">
                    <div
                        dangerouslySetInnerHTML={{
                            __html: this.formatIitialMessage(),
                        }}
                    ></div>
                </div>
            ),
        };
    };

    render() {
        const { type, message } = this.renderMessage();
        return (
            <Message className="h-24 mx-2 mt-1   relative " type={type}>
                {this.state.isLoading ? (
                    <div className=" w-56 mt-8 pr-5 flex absolute justify-center ">
                        <LoaderSpinner className="m-auto h-10  mb-2 items-center" variant="button"></LoaderSpinner>
                    </div>
                ) : (
                    <div className="flex flex-col w-full">
                        <div>{message}</div>
                        <div>{this.props.children}</div>
                    </div>
                )}
            </Message>
        );
    }
}

const ResultMessageStateToProps = (state: RootState) => ({
    port: state.port.port,
    mainControls: state.layout.mainControls,
    displayId: state.layout.layout.displayId,
    language: state.user.user.language,
    searchObject: state.layout.searchObject,
    searchControls: state.layout.searchControls,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ResultMessageDispatchToProps = () => ({});

export default connect(ResultMessageStateToProps, ResultMessageDispatchToProps)(ResultMessage);
